import type { ReactNode } from 'react'
import { useAuth } from 'react-oidc-context'
import { LoginScreen } from '../screens/auth/LoginScreen'

/**
 * ADR-0048 (Phase A, #7) — the route guard: wraps the whole authenticated app shell. Renders {@link
 * LoginScreen} for anyone not authenticated (never the app underneath — "logged out" and "app shell
 * visible" are mutually exclusive states here), a minimal loading state while `react-oidc-context`
 * is still resolving the session (initial load, or mid-redirect-callback), and surfaces any signin
 * error on the login screen itself rather than a blank page.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const auth = useAuth()

  if (auth.isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading"
        style={{
          display: 'grid',
          placeItems: 'center',
          minHeight: '100vh',
          color: 'var(--iw-text-muted)',
        }}
      >
        Loading…
      </div>
    )
  }

  if (auth.error) {
    return <LoginScreen errorMessage={auth.error.message} />
  }

  if (!auth.isAuthenticated) {
    return <LoginScreen />
  }

  return <>{children}</>
}
